/**
 * Geolocation and GDPR Region Detection Utilities
 * Detects user's country via IP and determines GDPR compliance requirements
 */

/** EU/EEA/UK country codes requiring GDPR compliance */
export const GDPR_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE', // EU members
  'IS',
  'LI',
  'NO', // EEA
  'GB', // UK (post-Brexit)
] as const

export const TEST_MODE_KEYS = {
  FORCE_EU: 'test_forceEU',
  FORCE_NON_EU: 'test_forceNonEU',
} as const

const GEOLOCATION_API = {
  URL: 'https://ipapi.co/json/',
  TIMEOUT_MS: 5000,
  CACHE_KEY: 'ipp_geolocation_cache',
  CACHE_DURATION_MS: 3600000, // 1 hour
} as const

interface CachedGeolocation {
  countryCode: string | null
  timestamp: number
}

export interface GeolocationResult {
  countryCode: string | null
  isEU: boolean
  requiresConsent: boolean
  detectionMethod: 'test_override' | 'ip_api' | 'cache' | 'unknown'
}

/** Check if country requires GDPR compliance */
export const isGDPRCountry = (countryCode: string | null): boolean => {
  if (!countryCode) return false
  return GDPR_COUNTRIES.includes(countryCode.toUpperCase() as (typeof GDPR_COUNTRIES)[number])
}

const getCachedGeolocation = (): string | null => {
  try {
    const cached = sessionStorage.getItem(GEOLOCATION_API.CACHE_KEY)
    if (!cached) return null

    const data: CachedGeolocation = JSON.parse(cached)
    const age = Date.now() - data.timestamp

    if (age > GEOLOCATION_API.CACHE_DURATION_MS) {
      sessionStorage.removeItem(GEOLOCATION_API.CACHE_KEY)
      return null
    }

    return data.countryCode
  } catch (error) {
    console.warn('[Geolocation] Error reading cache:', error)
    return null
  }
}

const setCachedGeolocation = (countryCode: string | null): void => {
  try {
    const data: CachedGeolocation = {
      countryCode,
      timestamp: Date.now(),
    }
    sessionStorage.setItem(GEOLOCATION_API.CACHE_KEY, JSON.stringify(data))
  } catch (error) {
    console.warn('[Geolocation] Error writing cache:', error)
  }
}

/** Detect user's country via IP (cached for 1 hour) */
export const detectCountryViaIP = async (): Promise<string | null> => {
  const cached = getCachedGeolocation()
  if (cached !== null) {
    console.log('[Geolocation] Using cached country:', cached)
    return cached
  }

  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Geolocation API timeout')), GEOLOCATION_API.TIMEOUT_MS)
    })

    const response = await Promise.race([
      fetch(GEOLOCATION_API.URL, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      timeoutPromise,
    ])

    if (!response.ok) {
      console.warn('[Geolocation] API returned error:', response.status)
      return null
    }

    const data = await response.json()
    const countryCode = data.country_code || null

    setCachedGeolocation(countryCode)
    console.log('[Geolocation] Detected country via IP:', countryCode)
    return countryCode
  } catch (error) {
    console.error('[Geolocation] Error detecting country:', error)
    return null
  }
}

/**
 * Check for testing overrides (?forceEU=true or ?forceNonEU=true)
 * Overrides persist in session storage across navigation
 */
export const checkTestOverrides = (): {
  isTestMode: boolean
  countryCode: string | null
  isEU: boolean
} => {
  const urlParams = new URLSearchParams(window.location.search)
  const forceEUParam = urlParams.get('forceEU') === 'true'
  const forceNonEUParam = urlParams.get('forceNonEU') === 'true'

  if (forceEUParam) {
    sessionStorage.setItem(TEST_MODE_KEYS.FORCE_EU, 'true')
    sessionStorage.removeItem(TEST_MODE_KEYS.FORCE_NON_EU)
  } else if (forceNonEUParam) {
    sessionStorage.setItem(TEST_MODE_KEYS.FORCE_NON_EU, 'true')
    sessionStorage.removeItem(TEST_MODE_KEYS.FORCE_EU)
  }

  const forceEU = sessionStorage.getItem(TEST_MODE_KEYS.FORCE_EU) === 'true'
  const forceNonEU = sessionStorage.getItem(TEST_MODE_KEYS.FORCE_NON_EU) === 'true'

  if (forceEU) {
    return { isTestMode: true, countryCode: 'DE', isEU: true }
  }

  if (forceNonEU) {
    return { isTestMode: true, countryCode: 'CA', isEU: false }
  }

  return { isTestMode: false, countryCode: null, isEU: false }
}

/**
 * Detect user's region and determine if consent is required
 * Checks test overrides → cached geolocation → IP detection
 */
export const detectUserRegion = async (): Promise<GeolocationResult> => {
  const testOverride = checkTestOverrides()
  if (testOverride.isTestMode) {
    console.log('[Geolocation] TEST MODE: Forcing region', {
      country: testOverride.countryCode,
      isEU: testOverride.isEU,
    })
    return {
      countryCode: testOverride.countryCode,
      isEU: testOverride.isEU,
      requiresConsent: testOverride.isEU,
      detectionMethod: 'test_override',
    }
  }

  const countryCode = await detectCountryViaIP()
  const isEU = isGDPRCountry(countryCode)

  if (countryCode) {
    return {
      countryCode,
      isEU,
      requiresConsent: isEU,
      detectionMethod: getCachedGeolocation() ? 'cache' : 'ip_api',
    }
  }

  console.warn('[Geolocation] Could not detect region - defaulting to non-EU (implied consent)')
  return {
    countryCode: null,
    isEU: false,
    requiresConsent: false,
    detectionMethod: 'unknown',
  }
}

export const clearGeolocationCache = (): void => {
  sessionStorage.removeItem(GEOLOCATION_API.CACHE_KEY)
  console.log('[Geolocation] Cache cleared')
}

export const clearTestOverrides = (): void => {
  sessionStorage.removeItem(TEST_MODE_KEYS.FORCE_EU)
  sessionStorage.removeItem(TEST_MODE_KEYS.FORCE_NON_EU)
  console.log('[Geolocation] Test overrides cleared')
}
